import { useRouter } from 'next/navigation';
import { useStore } from 'zustand';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { SearchType } from '@/types/most-search-type';
import {
  RecentSearches,
  SearchResult,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { Icon } from '@/components/ui/icon.component';
import {
  appendRecentSearches,
  setSelectedSearch,
} from '@/store/search/search.store';
import { useRecentSearchesStorage } from '@/storage/recents-searches.storage';
import { Avatar } from '@/components/ui/avatar/avatar.component';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { useSearch } from '@/app/_hooks/search/use-search.hook';
import { OverridedUserLocation } from '@/utils/formatters/city/format-place-from-slug.const';
import { userLocationStore } from '@/store/user-location.store';
import { setOpenLocationSelectionModal } from '@/store/modal';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { checkLaboratorialOrVaccineSearch } from '@/app/_utils/check-laboratorial-or-vaccine-search.util';
import {
  resolveIcon,
  resolveTitle,
} from '@/app/(main)/_components/search-modal/_utils/search-result-display.util';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

export { resolveTitle };

interface SearchResultTabProps extends SearchResult, RecentSearches {
  setOpen: (open: boolean) => void;
  selectLaboratoryProcedure?: (procedure: SelectedProcedures) => void;
  locationOverride?: OverridedUserLocation;
  inRecentSearch?: boolean;
  searchTerm?: string;
}

export const SearchResultTab = ({
  locationOverride,
  inRecentSearch,
  searchTerm,
  ...searchResult
}: SearchResultTabProps) => {
  const router = useRouter();
  const { handleJump: jump }
    = useProgressiveModal<ProgressiveSearchModalKeys>();
  const {
    resolveLink,
    resolveName,
    isProcedureSearch,
  } = useSearch({ locationOverride });
  const [, setStoredRecentSearches] = useRecentSearchesStorage();
  const userLocation = useStore(
    userLocationStore,
    (state) => state.userLocation,
  );
  const { getParam } = useSearchParams([
    ...Object.values(PersistedUserNavigationSearchParams),
  ]);

  const analytics = getAnalyticsPlugin();
  const isLaboratorialOrVaccineSearch = checkLaboratorialOrVaccineSearch(
    searchResult.searchType as SearchType,
  );
  const Component = isLaboratorialOrVaccineSearch ? 'button' : 'a';
  const linkWithParams = resolveLink(searchResult);
  const delay = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  const mapExamGroup = (item: SearchResult) => ({
    id: item.id!,
    code: item.code!,
    displayName: item.displayName!,
  });

  const handleLaboratorialFlow = async (): Promise<void> => {
    searchResult.setOpen(true);

    await delay(100);

    jump({
      key: inRecentSearch
        ? ProgressiveSearchModalKeys.SELECT_MORE_EXAMS
        : ProgressiveSearchModalKeys.ASK_ADD_MORE_EXAMS,
    });

    searchResult.selectLaboratoryProcedure?.({
      id: searchResult.id || '',
      code: searchResult.code || '',
      displayName: searchResult.displayName || '',
      examOrVaccineGroup: searchResult.examOrVaccineGroup?.map(mapExamGroup),
    });
  };

  const handleDefaultFlow = (): void => {
    appendRecentSearches(searchResult, setStoredRecentSearches);
    searchResult.setOpen(false);
  };

  const navigateToResult = (selectedLocation?: {
    city?: string;
    state?: string;
  }): void => {
    const link = resolveLink(searchResult);

    if (!selectedLocation) {
      router.push(link);
      return;
    }

    const url = new URL(link);
    url.searchParams.set(
      PersistedUserNavigationSearchParams.CIDADE,
      slugify(`${selectedLocation.city}-${selectedLocation.state}`),
    );

    router.push(url.toString());
  };

  const handleSearchResultClick = async (
    shouldNavigate = false,
    selectedLocation?: { city?: string; state?: string },
  ): Promise<void> => {
    await setSelectedSearch(searchResult);

    if (isLaboratorialOrVaccineSearch) {
      return handleLaboratorialFlow();
    }

    handleDefaultFlow();

    if (shouldNavigate) {
      navigateToResult(selectedLocation);
    }
  };

  const trackClick = (): void => {
    analytics.track(AnalyticsEnum.CLICK_RESULT_SEARCH_MODAL, {
      term: searchTerm || '',
      search_result_name: resolveName(searchResult),
    });
  };

  const shouldAskForLocation = (): boolean => {
    if (searchResult.searchType === SearchType.ONLINE_CONSULTATION) {
      return false;
    }

    const hasLocation
      = locationOverride
        || userLocation
        || getParam(PersistedUserNavigationSearchParams.CIDADE);

    const needsLocation = isProcedureSearch(
      searchResult.searchType as SearchType,
    );

    return !hasLocation && needsLocation;
  };

  const handleLocationRequired = async (e: React.MouseEvent): Promise<void> => {
    searchResult.setOpen(false);
    e.preventDefault();

    await delay(100);

    setOpenLocationSelectionModal(true, {
      locationCallback: async (selectedLocation) => {
        await handleSearchResultClick(
          !isLaboratorialOrVaccineSearch,
          selectedLocation,
        );
      },
    });
  };

  const handleDirectFlow = async (): Promise<void> => {
    if (isLaboratorialOrVaccineSearch) {
      return handleSearchResultClick();
    }

    return handleSearchResultClick(true);
  };

  const handleClick = async (e: React.MouseEvent): Promise<void> => {
    trackClick();

    if (shouldAskForLocation()) {
      return handleLocationRequired(e);
    }

    return handleDirectFlow();
  };

  return (
    <div>
      <Component
        data-testid={DataTestIdEnum.PROCEDURES_MODAL_SEARCH_RESULT}
        href={linkWithParams}
        className="text-left w-full"
        onClick={handleClick}
      >
        <div className="flex py-5 px-6 cursor-pointer">
          {!searchResult.avatar
            ? (
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 min-w-12 min-h-12 rounded-lg border bg-green-50 border-gray-300">
                  <Icon
                    icon={resolveIcon(searchResult)}
                    className="text-success-700 max-w-6 max-h-6"
                    size="lg"
                  />
                </div>
              )
            : (
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 min-w-12 min-h-12 rounded-lg">
                  <Avatar
                    src={searchResult.avatar}
                    alt={resolveName(searchResult)}
                    width={48}
                    height={48}
                  />
                </div>
              )}

          <div className="ml-4 flex flex-col">
            <Typography
              variant={TypographyVariant.SMALL_BODY_TEXT}
              className="text-green-500"
            >
              {resolveTitle(searchResult, inRecentSearch)}
            </Typography>
            <Typography
              className="text-gray-700 font-semibold"
            >
              {resolveName(searchResult)}
            </Typography>
          </div>
        </div>
      </Component>
    </div>
  );
};
